import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiLayout, FiBook, FiUsers, FiGlobe, FiCpu, FiHeadphones, 
  FiCheckCircle, FiHome, FiAward, FiTrendingUp, FiList, FiUser,
  FiBriefcase
} = FiIcons;

const Sidebar = ({ currentSection, setCurrentSection, progress, user }) => {
  const location = useLocation();
  const completedModules = Object.values(progress).filter(Boolean).length;
  const totalModules = Object.keys(progress).length;
  const progressPercent = Math.round((completedModules / totalModules) * 100);
  const isCompleted = progressPercent === 100;

  const navGroups = [
    {
      title: "Learning Path",
      items: [
        { id: 'introduction', path: '/', label: 'Dashboard', icon: FiLayout, description: 'Overview', color: 'from-purple-500 to-pink-500' },
        { id: 'syllabus', path: '/syllabus', label: 'Course Syllabus', icon: FiList, description: 'Curriculum', color: 'from-indigo-500 to-violet-500' },
        { id: 'nouns', path: '/nouns', label: 'Nouns & Gender', icon: FiBook, description: 'Grammar', color: 'from-blue-500 to-cyan-500' },
        { id: 'pronouns', path: '/pronouns', label: 'Pronouns', icon: FiUsers, description: 'Social Relations', color: 'from-green-500 to-emerald-500' },
        { id: 'cultural', path: '/cultural', label: 'Cultural Context', icon: FiGlobe, description: 'Applications', color: 'from-amber-500 to-orange-500' },
      ]
    },
    {
      title: "Practice Hub",
      items: [
        { id: 'exercises', path: '/exercises', label: 'Interactive Exercises', icon: FiCpu, description: 'Practice', color: 'from-rose-500 to-pink-500' },
        { id: 'audio', path: '/audio', label: 'Audio Gallery', icon: FiHeadphones, description: 'Listening', color: 'from-indigo-500 to-purple-500' },
        { id: 'reflection', path: '/reflection', label: 'Quiz & Reflection', icon: FiCheckCircle, description: 'Testing', color: 'from-emerald-500 to-teal-500' },
      ]
    },
    {
      title: "Resources",
      items: [
        { id: 'teacher-hub', path: '/teacher-hub', label: 'Teacher Hub', icon: FiBriefcase, description: 'Games & Printables', color: 'from-orange-500 to-red-500' },
      ]
    },
    {
      title: "Student Area",
      items: [
        { id: 'profile', path: '/profile', label: 'My Profile', icon: FiUser, description: 'Stats & Progress', color: 'from-cyan-500 to-blue-500' },
        { id: 'certificate', path: '/certificate', label: 'Certificate', icon: FiAward, description: 'Completion Award', color: 'from-yellow-400 to-orange-500', disabled: !isCompleted },
      ]
    }
  ];

  const getBadgeLevel = () => {
    if (progressPercent >= 100) return { icon: FiAward, level: 'Master', color: 'from-yellow-400 to-amber-500' };
    if (progressPercent >= 75) return { icon: FiTrendingUp, level: 'Advanced', color: 'from-purple-400 to-pink-500' };
    if (progressPercent >= 50) return { icon: FiTrendingUp, level: 'Intermediate', color: 'from-blue-400 to-cyan-500' };
    return { icon: FiHome, level: 'Beginner', color: 'from-gray-400 to-gray-500' };
  };

  const badge = getBadgeLevel();

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-slate-50 to-white border-r border-slate-200/60 backdrop-blur-xl">
      {/* Premium Brand Section */}
      <div className="h-20 flex items-center px-5 border-b border-slate-200/60 bg-gradient-to-r from-green-600 to-emerald-600 relative overflow-hidden shrink-0">
        <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10"></div>
        <div className="relative z-10 flex items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white shadow-2xl border border-white/30">
              <span className="font-bold text-xl font-amharic">ግ</span>
            </div>
            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full border-2 border-white shadow-sm"></div>
          </div>
          <div className="flex-1">
            <h1 className="font-bold text-white text-lg leading-none tracking-tight">Amharic</h1>
            <p className="text-green-50 text-xs font-medium mt-0.5">Social Grammar</p>
          </div>
        </div>
      </div>

      {/* Enhanced Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5 hide-scrollbar">
        {navGroups.map((group, idx) => (
          <div key={idx} className="space-y-2">
            <h3 className="px-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
              <div className="w-4 h-0.5 bg-gradient-to-r from-slate-300 to-transparent"></div>
              {group.title}
            </h3>
            <div className="space-y-1">
              {group.items.map((item) => {
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                const isCompleted = progress[item.id];
                const isDisabled = item.disabled;

                if (isDisabled) {
                  return (
                    <div key={item.id} className="relative flex items-center gap-3 px-3 py-2.5 rounded-xl opacity-50 cursor-not-allowed">
                       <div className="relative w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
                        <SafeIcon icon={item.icon} className="text-gray-400 text-sm" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm leading-tight text-gray-400">{item.label}</div>
                        <div className="text-[10px] text-gray-300">Locked</div>
                      </div>
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.id}
                    to={item.path}
                    onClick={() => setCurrentSection(item.id)}
                    className={`group relative block transition-all duration-300 ${isActive ? 'scale-[1.02]' : 'hover:scale-[1.01]'}`}
                  >
                    {/* Active Background */}
                    {isActive && (
                      <div className="absolute inset-0 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200/60 shadow-sm"></div>
                    )}
                    
                    <div className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-300 ${isActive ? 'text-green-700' : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'}`}>
                      {/* Icon Container */}
                      <div className={`relative w-8 h-8 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center shadow-md transition-all duration-300 ${isActive ? 'scale-105 shadow-lg' : 'group-hover:scale-105'}`}>
                        <SafeIcon icon={item.icon} className="text-white text-sm relative z-10" />
                        <div className="absolute inset-0 bg-white/20 rounded-lg backdrop-blur-sm"></div>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm leading-tight">{item.label}</div>
                        <div className="text-[10px] opacity-70 hidden lg:block">{item.description}</div>
                      </div>

                      {/* Completion Badge */}
                      {isCompleted && (
                        <div className="relative">
                          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-sm">
                            <SafeIcon icon={FiCheckCircle} className="text-white text-[10px]" />
                          </div>
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* User Progress Footer */}
      <div className="p-3 border-t border-slate-200/60 bg-gradient-to-r from-slate-50 to-white shrink-0">
        <div className="bg-white/80 backdrop-blur-sm p-3 rounded-xl border border-slate-200/60 shadow-sm">
          {/* Header */}
          <div className="flex justify-between items-center mb-2">
            <div>
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <div className="w-6 h-0.5 bg-gradient-to-r from-green-400 to-emerald-400"></div>
                Progress
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-lg font-bold text-slate-800">{progressPercent}%</span>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-2">
            <div 
              className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
          
          <div className="flex justify-between items-center text-xs text-slate-500">
            <span>{completedModules}/{totalModules} Modules</span>
            <div className="flex items-center gap-1">
              <SafeIcon icon={badge.icon} className={`text-xs bg-gradient-to-r ${badge.color} bg-clip-text text-transparent`} />
              <span className="font-medium">{badge.level}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;